import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { startPlanCheckout } from "@/lib/planCheckout";
import { toast } from "sonner";
import { PawPrint, Lock, ShieldCheck, ArrowRight, Loader2, AlertTriangle, XCircle } from "lucide-react";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function Checkout() {
  const { refreshMe } = useAuth();
  const navigate = useNavigate();
  // idle | preparing | verifying | cancelled | failed | startingfree
  const [phase, setPhase] = useState("idle");

  const { data: state, isLoading } = useQuery({
    queryKey: ["signup-state"],
    queryFn: async () => (await api.get("/saas/signup-state")).data,
    refetchOnWindowFocus: false,
  });

  // Authoritative recovery: if the server says payment is no longer required (paid via
  // webhook recovery, already free, or already onboarded) move forward — never pay twice.
  useEffect(() => {
    if (!state || state.requires_payment) return;
    (async () => {
      await refreshMe();
      navigate(state.onboarded ? "/dashboard" : "/onboarding", { replace: true });
    })();
  }, [state]); // eslint-disable-line react-hooks/exhaustive-deps

  const pay = () => {
    setPhase("preparing");
    startPlanCheckout({ planCode: state.plan_code, interval: state.billing_interval, theme: "#6A48F0", onResult: async ({ state: s, message }) => {
      if (s === "open") setPhase("idle");
      if (s === "error") { setPhase("failed"); toast.error(message); }
      if (s === "verifying") setPhase("verifying");
      if (s === "cancelled") setPhase("cancelled");
      if (s === "failed") setPhase("failed");
      if (s === "unverified") { setPhase("failed"); toast.error(message || "Payment verification failed"); }
      if (s === "paid_pending") { setPhase("idle"); toast.message("Payment received — activation is being completed. Please refresh shortly."); }
      if (s === "activated") {
        await refreshMe();
        toast.success("Payment successful. Let's set up your business.");
        navigate("/onboarding", { replace: true });
      }
    } });
  };

  const startFree = async () => {
    setPhase("startingfree");
    try {
      await api.post("/saas/signup/start-free");
      await refreshMe();
      toast.success("You're on the Free plan. Let's set up your business.");
      navigate("/onboarding", { replace: true });
    } catch (e) { setPhase("idle"); toast.error(apiErr(e)); }
  };

  if (isLoading || !state || !state.requires_payment) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50" data-testid="checkout-loading">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const a = state.amounts || {};
  const busy = phase === "preparing" || phase === "verifying" || phase === "startingfree";

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="checkout-page">
      <div className="border-b border-slate-200 bg-white px-6 h-16 flex items-center gap-2.5">
        <div className="h-9 w-9 rounded-xl bg-slate-900 flex items-center justify-center"><PawPrint className="h-5 w-5 text-emerald-400" /></div>
        <span className="font-display text-lg font-extrabold tracking-tight">PAWKT</span>
        <span className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-slate-400"><Lock className="h-3.5 w-3.5" /> Secure checkout</span>
      </div>

      <div className="flex-1 flex items-start justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Complete your payment</h1>
          <p className="text-slate-500 mt-1">Activate your <span className="font-semibold capitalize text-slate-700">{state.plan_name}</span> plan to continue setting up your business.</p>

          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="checkout-summary">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-800">{state.plan_name} plan</span>
              <span className="text-xs font-medium text-slate-400 capitalize">{state.billing_interval}</span>
            </div>
            <div className="mt-4 space-y-2 text-sm">
              <Row label="Plan price" value={inr(a.amount)} testid="checkout-base" />
              <Row label={`GST (${a.gst_rate ?? 18}%)`} value={inr(a.tax_amount)} testid="checkout-gst" />
              <div className="my-2 h-px bg-slate-100" />
              <div className="flex items-center justify-between">
                <span className="font-bold text-slate-900">Total payable</span>
                <span className="font-display text-xl font-extrabold text-slate-900" data-testid="checkout-total">{inr(a.total_amount)}</span>
              </div>
            </div>
          </div>

          {phase === "cancelled" && (
            <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800" data-testid="checkout-cancelled">
              <XCircle className="h-4 w-4 mt-0.5 shrink-0" /> Payment cancelled. Complete payment to continue onboarding.
            </div>
          )}
          {phase === "failed" && (
            <div className="mt-4 flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800" data-testid="checkout-failed">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> Payment could not be completed. Please complete payment to continue onboarding.
            </div>
          )}

          <button onClick={pay} disabled={busy} data-testid="checkout-pay-btn"
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3 text-sm font-bold text-white shadow-purpleGlow hover:bg-pawkt-primary-hover disabled:opacity-60">
            {phase === "verifying" ? <><Loader2 className="h-4 w-4 animate-spin" /> Verifying payment…</>
              : phase === "preparing" ? <><Loader2 className="h-4 w-4 animate-spin" /> Preparing secure payment…</>
              : <>{(phase === "cancelled" || phase === "failed") ? "Retry Payment" : `Pay ${inr(a.total_amount)} securely`} <ArrowRight className="h-4 w-4" /></>}
          </button>

          <button onClick={startFree} disabled={busy} data-testid="checkout-start-free-btn"
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-slate-200 bg-white py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60">
            {phase === "startingfree" ? <><Loader2 className="h-4 w-4 animate-spin" /> Switching…</> : "Start on Free instead"}
          </button>

          <p className="mt-4 inline-flex items-center gap-1.5 text-xs text-slate-400"><ShieldCheck className="h-3.5 w-3.5" /> Payments are processed securely by Razorpay. We never store your card details.</p>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between text-slate-600">
      <span>{label}</span><span className="font-medium text-slate-800" data-testid={testid}>{value}</span>
    </div>
  );
}
